import {
  Avatar,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { generatePath } from 'react-router-dom'

import {
  ArtistFavoriteMember,
  ArtistMembershipFavoriteMembersSelectQuery,
  Avatar as AvatarType,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { MembershipFavoriteMemberSelect } from '@/components/organisms/membership/membershipFavoriteMembersSelect'
import { MembershipJoinedModal } from '@/components/organisms/membership/membershipJoinedModal'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { CellFrame, Template } from '@/core/architecture'

export type State = {
  artist: Maybe<ArtistMembershipFavoriteMembersSelectQuery['artist']>
  artistId: string
  selectedFavoriteMember?: string
  favoriteMembers:
    | Maybe<
        Maybe<
          Pick<ArtistFavoriteMember, 'name' | 'id'> & {
            avatar: Maybe<Pick<AvatarType, 'uri'>>
          }
        >[]
      >
    | undefined
  loading: boolean
  joined: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: () => Promise<void>
  onSelectFavoriteMember: (memberId: string) => void
}

export const ArtistMembershipFavoriteMembersSelectTemplate: Template<
  State,
  Action
> = ({ state, onSubmit, onSelectFavoriteMember }) => {
  const { t } = useTranslation('artistMembershipFavoriteMembersSelect')
  const { router, routerNames, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const defaultBackPage = generatePath(routerPages.ArtistMembership, {
    artistId: state.artistId,
  })

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ArtistMembershipFavoriteMembersSelect} />
      {state.joined ? (
        <MembershipJoinedModal artistId={state.artistId} />
      ) : (
        <BaseLayout
          header={{
            back: true,
            defaultBackPage: defaultBackPage,
            title: t('title', '推し設定'),
          }}
        >
          <Card
            elevation={0}
            sx={{ borderRadius: 2, bgcolor: 'grey.50', m: 5 }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" alignItems="center">
                <Avatar
                  src={state.artist?.avatar?.uri ?? ''}
                  sx={{ width: 44, height: 44, m: 2 }}
                />
                <Typography variant="body1" sx={{ width: 'fit-content' }}>
                  {state.artist?.displayName}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
          <CellFrame sx={{ m: 5, height: '320px', overflowY: 'auto' }}>
            <MembershipFavoriteMemberSelect
              favoriteMembers={state.favoriteMembers ?? []}
              selectedFavoriteMember={state.selectedFavoriteMember}
              onSelectFavoriteMember={onSelectFavoriteMember}
            />
          </CellFrame>
          <Stack sx={{ alignItems: 'center' }}>
            <CellFrame
              sx={{
                mx: 5,
                mb: 1.5,
                p: 2,
                border: 'solid 1px',
                borderColor: 'info.main',
                width: '50%',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="info.main">
                {t('note', '※ 推しは設定から90日間変更できません')}
              </Typography>
            </CellFrame>
          </Stack>
          <Stack sx={{ my: 5 }} alignItems="center" gap={4}>
            <Button
              size="large"
              disabled={!state.selectedFavoriteMember}
              loading={state.loading}
              sx={{ width: 212 }}
              onClick={onSubmit}
            >
              {t('submit', '支払い方法選択に進む')}
            </Button>
            <Button
              variant="text"
              size="small"
              sx={{ width: 212 }}
              onClick={() => router.goBack(defaultBackPage)}
            >
              {t('back', '戻る')}
            </Button>
          </Stack>
        </BaseLayout>
      )}
    </ArtistThemeProvider>
  )
}
